import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Sparkles, ArrowRight, Star } from 'lucide-react';
import { FloatingDoodles } from '../components/Doodles';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    const result = await register(email, password, name);
    setLoading(false);
    
    if (result.success) {
      navigate('/choose-pet');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFAF0] flex items-center justify-center p-4 relative">
      <FloatingDoodles />
      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#B9FBC0] border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] mb-4">
            <Star className="w-10 h-10 text-black" strokeWidth={2.5} />
          </div>
          <h1 className="text-4xl font-bold font-['Fredoka'] text-gray-900">Join Focus Critter</h1>
          <p className="text-gray-600 mt-2 font-['Nunito']">Start your focus journey with a cute companion!</p>
        </div>

        {/* Register Card */}
        <div className="neo-card p-8">
          <h2 className="text-2xl font-bold font-['Fredoka'] mb-6 text-center">Create Account</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-[#FF6B6B] text-white rounded-lg border-2 border-black font-semibold" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="name">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="neo-input w-full"
                placeholder="What should we call you?"
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="neo-input w-full"
                placeholder="your@email.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="neo-input w-full"
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="neo-btn w-full py-3 bg-[#B9FBC0] text-black flex items-center justify-center gap-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span>Creating account...</span>
              ) : (
                <>
                  <span>Start My Journey!</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">eller</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <GoogleSignInButton label="Registrera med Google" testId="register-google-btn" />

          <div className="mt-6 text-center">
            <p className="text-gray-600 font-['Nunito']">
              Already have an account?{' '}
              <Link to="/login" className="text-[#A2D2FF] font-bold hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Fun decoration */}
        <div className="flex justify-center gap-4 mt-8">
          <Heart className="w-6 h-6 text-[#FFC8DD]" />
          <Sparkles className="w-6 h-6 text-[#A2D2FF]" />
          <Heart className="w-6 h-6 text-[#CDB4DB]" />
        </div>
      </div>
    </div>
  );
}
